import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_EXPIRED_EVENT = 'auth:session-expired';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    const stored = localStorage.getItem('token');
    if (stored) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${stored}`;
    }
    return stored;
  });
  
  // Ref to prevent multiple fetchUser calls
  const isFetchingUser = useRef(false);
  // Ref to prevent repeated 401 redirect loops on token expiry
  const isRedirecting = useRef(false);

  const clearAuthState = () => {
    if (isRedirecting.current) {
      return;
    }

    isRedirecting.current = true;
    setUser(null);
    setToken(null);
    setSubscriptionData(null);
    localStorage.removeItem('token');
    sessionStorage.clear();
    delete axios.defaults.headers.common['Authorization'];
    window.location.replace('/');
  };

  useEffect(() => {
    const handleSessionExpired = () => {
      clearAuthState();
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, []);

  // Global 401 handler: clear auth and force redirect
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const isLoginRequest = error.config?.url?.includes('/auth/login');
          const hasToken = !!localStorage.getItem('token');

          // Redirect once only for authenticated sessions that actually expired
          if (!isLoginRequest && hasToken && !isRedirecting.current) {
            clearAuthState();
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Combined effect: Update localStorage, axios headers, and fetch user when token changes
  useEffect(() => {
    if (!token) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setSubscriptionData(null);
      setLoading(false);
      return;
    }

    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchUser();
  }, [token]);

  const fetchUser = async () => {
    // Prevent multiple simultaneous fetchUser calls
    if (isFetchingUser.current) {
      return;
    }

    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      setLoading(false);
      return;
    }

    isFetchingUser.current = true;
    
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      setUser(userData);
      setSubscriptionData(userData.subscription_data || {});
    } catch (error) {
      // 401 is handled by interceptor; avoid second redirect loop here
      if (error.response?.status !== 401) {
        setToken(null);
        setUser(null);
        setSubscriptionData(null);
      }
    } finally {
      setLoading(false);
      isFetchingUser.current = false;
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData, subscription_data } = response.data;
      
      // Store token synchronously immediately so post-login navigation has valid credentials
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      setToken(access_token);
      setUser(userData);
      setSubscriptionData(subscription_data);
      
      return userData;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Login failed';
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'Login failed');
    }
  };

  const loginWithGoogle = async () => {
    try {
      // Get Google OAuth URL
      const response = await axios.get(`${API}/auth/google/login`);
      const { auth_url } = response.data;
      
      // Redirect to Google OAuth
      window.location.href = auth_url;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to initiate Google login';
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'Failed to initiate Google login');
    }
  };

  const register = async (email, password, full_name, phone, degree, passout_year, branch, state, city) => {
    const response = await axios.post(`${API}/auth/register`, { 
      email, 
      password, 
      full_name, 
      phone, 
      degree, 
      passout_year, 
      branch, 
      state, 
      city 
    });
    // Don't set token immediately as user needs to verify email first
    return response;
  };

  // refreshSubscriptionData function completely disabled to prevent continuous /auth/me calls
  const refreshSubscriptionData = async () => {
    console.log('refreshSubscriptionData called but function is disabled');
    return; // Do nothing to prevent continuous API calls
  };

  const logout = async () => {
    // Clear local state immediately for instant logout
    clearAuthState();
    
    // Call backend logout API asynchronously (fire and forget)
    axios.post(`${API}/auth/logout`)
      .catch(error => {
        // Log error but don't affect user experience
        console.error('Logout API error:', error);
      });

    // Force full reload to clear any protected UI in memory
    window.location.replace('/');
  };

  const updateUser = (newData) => {
    setUser((prev) => (prev ? { ...prev, ...newData } : newData));
  };

  return (
    <AuthContext.Provider value={{ user, subscriptionData, login, loginWithGoogle, register, logout, loading, setToken, refreshSubscriptionData, updateUser, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
